import { useChat } from '../../contexts/ChatContext';
import styles from './Sidebar.module.css';

export function Sidebar() {
  const {
    sessions,
    activeSession,
    isLoadingSessions,
    selectSession,
    deleteSession,
    newSession,
  } = useChat();

  return (
    <nav className={styles.sidebar} aria-label="Chat history">
      <div className={styles.top}>
        <button
          className={styles.newChatBtn}
          onClick={newSession}
          aria-label="Start a new conversation"
        >
          <PlusIcon />
          New conversation
        </button>
      </div>

      <div className={styles.sessionList}>
        {isLoadingSessions && (
          <div className={styles.loading} aria-live="polite">
            <LoadingDots />
          </div>
        )}

        {!isLoadingSessions && sessions.length === 0 && (
          <p className={styles.empty}>No conversations yet</p>
        )}

        {sessions.map((session) => (
          <SessionItem
            key={session.sessionId}
            session={session}
            isActive={activeSession?.sessionId === session.sessionId}
            onSelect={() => selectSession(session.sessionId)}
            onDelete={() => deleteSession(session.sessionId)}
          />
        ))}
      </div>
    </nav>
  );
}

interface SessionItemProps {
  session: { sessionId: string; title: string; updatedAt: string; messageCount: number };
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

function SessionItem({ session, isActive, onSelect, onDelete }: SessionItemProps) {
  const formattedDate = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(new Date(session.updatedAt));

  return (
    <div
      className={`${styles.sessionItem} ${isActive ? styles.active : ''}`}
      role="button"
      tabIndex={0}
      aria-selected={isActive}
      aria-label={session.title}
      onClick={onSelect}
      onKeyDown={(e) => e.key === 'Enter' && onSelect()}
    >
      <div className={styles.sessionContent}>
        <span className={styles.sessionTitle}>{session.title}</span>
        <span className={styles.sessionMeta}>
          {formattedDate} · {session.messageCount} msg{session.messageCount !== 1 ? 's' : ''}
        </span>
      </div>
      <button
        className={styles.deleteBtn}
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        aria-label={`Delete conversation: ${session.title}`}
        title="Delete conversation"
      >
        <TrashIcon />
      </button>
    </div>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4h6v2" />
    </svg>
  );
}

function LoadingDots() {
  return <span className={styles.dots} aria-label="Loading">···</span>;
}
